import { InvestmentAllocation } from '../../../types';
import {
  FooterCell,
  FooterContainer,
  HeaderCell,
  StyledHeader,
  StyledRow,
  StyledTable,
} from 'components/TableComponents/TableComponents';
import { styled, TableBody, TableCell, TableRow } from '@mui/material';
import { groupBy, sumBy } from 'lodash';
import { numberToCurrencyString } from 'further-ui/utils';
import { CurrencyCode } from 'further-types/lp/vehicle';

const DarkTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.background.darkTableCell,
  fontWeight: theme.typography.fontWeightBold,
}));

const DarkFooterCell = styled(FooterCell)(({ theme }) => ({
  backgroundColor: theme.palette.background.darkTableCell,
}));

const AutoWidthHeaderCell = styled(HeaderCell)(() => ({
  whiteSpace: 'nowrap',
  width: 'auto',
}));

type TotalsTableProps = {
  currency: CurrencyCode;
  investmentAllocation: Array<InvestmentAllocation>;
};

const TotalsTable: React.FC<TotalsTableProps> = ({
  currency,
  investmentAllocation,
}) => {
  const allocationsByInvestorAndCommitment = groupBy(
    investmentAllocation,
    (allocation) => allocation.commitmentId,
  );

  const investorCommitmentTotals = Object.entries(
    allocationsByInvestorAndCommitment,
  ).map(([key, allocations]) => {
    const commitmentId = key;
    const receivedTotal = sumBy(allocations, 'receivedConsideration');
    const escrowTotal = sumBy(allocations, 'escrowConsideration');
    const earnoutTotal = sumBy(allocations, 'earnoutConsideration');
    const withholdingTotal = sumBy(allocations, 'withholdingTaxPayable');

    return {
      commitmentId,
      receivedTotal,
      escrowTotal,
      earnoutTotal,
      withholdingTotal,
    };
  });

  const grandTotals = {
    received: sumBy(investorCommitmentTotals, 'receivedTotal'),
    escrow: sumBy(investorCommitmentTotals, 'escrowTotal'),
    earnout: sumBy(investorCommitmentTotals, 'earnoutTotal'),
    withholding: sumBy(investorCommitmentTotals, 'withholdingTotal'),
  };

  return (
    <StyledTable
      isTotalsTable
      hasBorderBottomRightRadius
      hasBorderTopRightRadius
    >
      <StyledHeader>
        <StyledRow>
          <AutoWidthHeaderCell
            colSpan={4}
            align="center"
            isHeader
            textSize="regular"
          >
            Total consideration
          </AutoWidthHeaderCell>
        </StyledRow>

        <StyledRow>
          <AutoWidthHeaderCell
            backgroundColor="paper"
            isHeader
            paddingSize="md"
          >
            Total received consideration
          </AutoWidthHeaderCell>
          <AutoWidthHeaderCell
            backgroundColor="paper"
            isHeader
            paddingSize="md"
          >
            Total escrow consideration
          </AutoWidthHeaderCell>
          <AutoWidthHeaderCell
            backgroundColor="paper"
            isHeader
            paddingSize="md"
          >
            Total earnout consideration
          </AutoWidthHeaderCell>
          <AutoWidthHeaderCell
            backgroundColor="paper"
            isHeader
            paddingSize="md"
          >
            Total withholding tax
          </AutoWidthHeaderCell>
        </StyledRow>
      </StyledHeader>
      <TableBody>
        {investorCommitmentTotals.map(
          ({
            commitmentId,
            receivedTotal,
            escrowTotal,
            earnoutTotal,
            withholdingTotal,
          }) => (
            <StyledRow key={commitmentId}>
              <DarkTableCell>
                {numberToCurrencyString(receivedTotal, {
                  currency,
                })}
              </DarkTableCell>
              <DarkTableCell>
                {numberToCurrencyString(escrowTotal, {
                  currency,
                })}
              </DarkTableCell>
              <DarkTableCell>
                {numberToCurrencyString(earnoutTotal, {
                  currency,
                })}
              </DarkTableCell>
              <DarkTableCell>
                {numberToCurrencyString(withholdingTotal, {
                  currency,
                })}
              </DarkTableCell>
            </StyledRow>
          ),
        )}
      </TableBody>
      <FooterContainer>
        <TableRow>
          <DarkFooterCell>
            {numberToCurrencyString(grandTotals.received, {
              currency,
            })}
          </DarkFooterCell>
          <DarkFooterCell>
            {numberToCurrencyString(grandTotals.escrow, {
              currency,
            })}
          </DarkFooterCell>
          <DarkFooterCell>
            {numberToCurrencyString(grandTotals.earnout, {
              currency,
            })}
          </DarkFooterCell>
          <DarkFooterCell>
            {numberToCurrencyString(grandTotals.withholding, {
              currency,
            })}
          </DarkFooterCell>
        </TableRow>
      </FooterContainer>
    </StyledTable>
  );
};

export default TotalsTable;
